import type { MonthValue } from '@/src/types/monthValue'
import { MONTHS_OPTIONS } from '@/src/utils/constants'
import { Flex } from '@chakra-ui/react'
import { format } from 'date-fns'
import { enUS } from 'date-fns/locale'
import type { ReactDatePickerCustomHeaderProps } from 'react-datepicker'
import { ChevronLeft, ChevronRight } from 'react-feather'
import { DatePickerMonthDropdown } from './DatePickerMonthDropdown'
import { DatePickerYearDropdown } from './DatePickerYearDropdown'
import type { DatePickerType } from './datePicker.types'

interface DatePickerHeaderProps extends ReactDatePickerCustomHeaderProps {
  pickerType: DatePickerType
  minDate?: Date
  maxDate?: Date
  disabledYears?: number[]
}

export const DatePickerHeader = ({
  monthDate,
  decreaseMonth,
  increaseMonth,
  increaseYear,
  decreaseYear,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
  prevYearButtonDisabled,
  nextYearButtonDisabled,
  pickerType,
  minDate,
  maxDate,
  disabledYears,
}: DatePickerHeaderProps) => {
  const handleMonthChange = (newMonth: string) => {
    const monthIndex = MONTHS_OPTIONS.findIndex(m => m.value === newMonth)

    const monthsToMove = monthIndex - monthDate.getMonth()
    if (monthsToMove > 0) {
      for (let i = 0; i < monthsToMove; i++) {
        increaseMonth()
      }
    } else {
      for (let i = 0; i > monthsToMove; i--) {
        decreaseMonth()
      }
    }
  }

  const handleYearChange = (newYear: string) => {
    const currentYear = monthDate.getFullYear()
    const targetYear = Number.parseInt(newYear)
    const yearsToMove = targetYear - currentYear

    if (yearsToMove > 0) {
      for (let i = 0; i < yearsToMove; i++) {
        increaseYear()
      }
    } else {
      for (let i = 0; i > yearsToMove; i--) {
        decreaseYear()
      }
    }
  }

  if (pickerType === 'year') {
    return null
  }

  const currentMonthValue = format(monthDate, 'MMMM', { locale: enUS }).toLowerCase() as MonthValue
  const currentYearValue = format(monthDate, 'yyyy')

  const showYearDropdown =
    pickerType === 'day' || pickerType === 'week' || pickerType === 'month' || pickerType === 'quarter' || pickerType === 'datetime'

  const showMonthDropdown = pickerType === 'day' || pickerType === 'week' || pickerType === 'datetime'

  const buttonHandlers = showMonthDropdown
    ? { decrease: decreaseMonth, increase: increaseMonth, nextDisabled: nextMonthButtonDisabled, prevDisabled: prevMonthButtonDisabled }
    : { decrease: decreaseYear, increase: increaseYear, nextDisabled: nextYearButtonDisabled, prevDisabled: prevYearButtonDisabled }

  return (
    <Flex justifyContent={'space-around'} px='12px' gap='16px'>
      <Flex gap='8px' grow={1}>
        {showMonthDropdown && (
          <DatePickerMonthDropdown
            value={currentMonthValue}
            onChange={handleMonthChange}
            minDate={minDate}
            maxDate={maxDate}
            currentYear={monthDate.getFullYear()}
          />
        )}
        {showYearDropdown && (
          <DatePickerYearDropdown
            value={currentYearValue}
            onChange={handleYearChange}
            minDate={minDate}
            maxDate={maxDate}
            disabledYears={disabledYears}
          />
        )}
      </Flex>

      <Flex gap='8px'>
        <button onClick={buttonHandlers.decrease} disabled={buttonHandlers.prevDisabled} type='button' role='button' title='Previous month'>
          <ChevronLeft color={buttonHandlers.prevDisabled ? '#B4B4B4' : 'black'} />
        </button>
        <button onClick={buttonHandlers.increase} disabled={buttonHandlers.nextDisabled} type='button' role='button' title='Next month'>
          <ChevronRight color={buttonHandlers.nextDisabled ? '#B4B4B4' : 'black'} />
        </button>
      </Flex>
    </Flex>
  )
}
